import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  CheckCircle, XCircle, Clock, Monitor, AlertCircle,
  AlertTriangle, Search, RotateCcw, Wifi, X, ArrowRight, Download,
} from "lucide-react";
import styles from "../hrms.module.scss";
import { APIConstant } from "services/api-constant";
import { apiBaseUrl } from "services/config";
import { useTheme } from "context/ThemeContext";

const COLORS = ["#00C8FF","#7C5CFF","#00E5A3","#FFB800","#FF4D6D","#3B82F6"];
const initials = (f="", l="") => `${f[0]||""}${l[0]||""}`.toUpperCase();

const getLocalDate = () => {
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
};

const parseLocalDate = (dateStr) => {
  if (!dateStr) return new Date();
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const getAuthToken = () => (
  sessionStorage.getItem("access_token") ||
  sessionStorage.getItem("platform_token") ||
  (() => {
    try { return JSON.parse(sessionStorage.getItem("platform_user") || "{}").access_token; }
    catch { return ""; }
  })() || ""
);

const APP_ICONS = {
  "Google Chrome":"🌐","Chrome":"🌐","Safari":"🧭","Firefox":"🦊",
  "Visual Studio Code":"💻","VS Code":"💻","Code":"💻",
  "Terminal":"⬛","iTerm2":"⬛","cmd.exe":"⬛",
  "Slack":"💬","Microsoft Teams":"💬","Zoom":"📹",
  "Figma":"🎨","Sketch":"🎨","Xcode":"🔨","Android Studio":"🤖",
  "Postman":"📬","Insomnia":"📬","Excel":"📊","Numbers":"📊",
  "Word":"📝","Pages":"📝","Notion":"📝",
  "Spotify":"🎵","YouTube":"▶️","Mail":"📧","Outlook":"📧",
};
const APP_COLORS = ["#00C8FF","#7C5CFF","#00E5A3","#FFB800","#FF4D6D","#3B82F6","#EC4899","#F59E0B"];
const getAppIcon = (name) => {
  const key = Object.keys(APP_ICONS).find(k => name?.toLowerCase().includes(k.toLowerCase()));
  return key ? APP_ICONS[key] : "🖥";
};


// ── Actual Hours Cell ─────────────────────────────────────────────
function ActualHoursCell({ employeeId, empName, date }) {
  const { T } = useTheme();
  const [data,    setData]    = useState(null);
  const [apps,    setApps]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [open,    setOpen]    = useState(false);
  const [error,   setError]   = useState("");

  const fmtSecs = (s) => {
    const h = Math.floor((s||0) / 3600);
    const m = Math.floor(((s||0) % 3600) / 60);
    return `${h}h ${m}m`;
  };
  const fmtTime = (iso, showDate = false) => {
    if (!iso) return "—";
    const d = new Date(iso);
    const time = d.toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit" });
    if (!showDate) return time;
    const dt = d.toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" });
    return `${dt}\n${time}`;
  };

  const load = async () => {
    setLoading(true); setError("");
    try {
      const token = getAuthToken();
      const hdr   = { Authorization: `Bearer ${token}` };
      const [sumRes, appRes] = await Promise.all([
        fetch(`${apiBaseUrl}/presence/daily-report?employee_id=${employeeId}&from_date=${date}&to_date=${date}`, { headers: hdr }),
        fetch(`${apiBaseUrl}/presence/app-breakdown?employee_id=${employeeId}&date=${date}`, { headers: hdr }),
      ]);
      if (!sumRes.ok) { const e = await sumRes.json().catch(()=>({})); throw new Error(e.detail || `daily-report ${sumRes.status}`); }
      if (!appRes.ok) { const e = await appRes.json().catch(()=>({})); throw new Error(e.detail || `app-breakdown ${appRes.status}`); }
      const sumJson = await sumRes.json();
      const appJson = await appRes.json();
      setData(Array.isArray(sumJson) && sumJson.length > 0 ? sumJson[0] : null);
      setApps(Array.isArray(appJson) ? appJson : []);
    } catch(e) {
      console.error("[ActualHoursCell]", e.message);
      setError(e.message); setData(null); setApps([]);
    }
    finally { setLoading(false); setOpen(true); }
  };

  const secs  = data?.active_seconds || 0;
  const hasData = secs > 0 || apps.length > 0;
  const pct   = Math.min(100, (secs / 28800) * 100);

  return (
    <>
      <button onClick={load} disabled={loading}
        style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 10px",
          borderRadius:7, border:`1px solid ${secs>0?`${T.cyan}40`:T.toggle_bg}`,
          background:secs>0?`${T.cyan}0F`:"none",
          color:loading?T.dim:secs>0?T.cyan:T.muted,
          cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:12, fontWeight:600 }}>
        <Monitor size={11}/>
        {loading ? "Loading…" : secs>0 ? fmtSecs(secs) : "View"}
      </button>

      {open && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", zIndex:9999,
          display:"flex", alignItems:"center", justifyContent:"center",
          fontFamily:"Outfit,sans-serif", padding:20 }}
          onClick={e => { if(e.target===e.currentTarget) setOpen(false); }}>
          <div style={{ background:T.card, border:`1px solid ${T.cyan}33`,
            borderRadius:16, width:560, maxHeight:"88vh", overflow:"hidden",
            display:"flex", flexDirection:"column", boxShadow:T.shadow }}>
            <div style={{ padding:"18px 24px", background:T.card2,
              borderBottom:`1px solid ${T.border}`, flexShrink:0,
              display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ fontSize:10, color:T.cyan, fontWeight:700,
                  textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:4 }}>
                  🖥 Desktop Activity Report
                </div>
                <div style={{ fontSize:17, fontWeight:800, color:T.text }}>{empName}</div>
                <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>
                  {parseLocalDate(date).toLocaleDateString(undefined, {
                    weekday:"long", day:"2-digit", month:"long", year:"numeric" })}
                </div>
              </div>
              <button onClick={() => setOpen(false)}
                style={{ background:T.toggle_bg, border:"none", borderRadius:"50%",
                  width:34, height:34, cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  color:T.muted, flexShrink:0 }}>
                <X size={15}/>
              </button>
            </div>
            <div style={{ overflowY:"auto", flex:1, padding:22 }}>
              {error ? (
                <div style={{ textAlign:"center", padding:"32px 0" }}>
                  <div style={{ fontSize:28, marginBottom:12 }}>⚠️</div>
                  <div style={{ fontSize:13, fontWeight:600, color:T.red, marginBottom:8 }}>
                    Could not load activity data
                  </div>
                  <div style={{ fontSize:11, color:T.dim, fontFamily:"JetBrains Mono,monospace",
                    background:`${T.red}0F`, border:`1px solid ${T.red}26`,
                    borderRadius:8, padding:"8px 14px", display:"inline-block" }}>
                    {error}
                  </div>
                </div>
              ) : !hasData ? (
                <div style={{ textAlign:"center", padding:"40px 0" }}>
                  <Monitor size={44} style={{ color:T.dim, marginBottom:14 }}/>
                  <div style={{ fontSize:14, fontWeight:600, color:T.muted }}>No activity recorded</div>
                  <div style={{ fontSize:12, color:T.dim, marginTop:8, lineHeight:1.8 }}>
                    Desktop agent may not be installed,<br/>or this employee hasn't started it today.
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:20 }}>
                    {[
                      { label:"Active Time", val:fmtSecs(secs), color:T.cyan, big:true },
                      { label:"Last Seen",   val:fmtTime(data?.last_seen,true), color:T.amber, big:false },
                    ].map(({label,val,color,big}) => (
                      <div key={label} style={{ background:T.toggle_bg, borderRadius:10,
                        padding:"14px", textAlign:"center" }}>
                        <div style={{ fontSize:9.5, color:T.muted, marginBottom:6,
                          textTransform:"uppercase", letterSpacing:"0.08em" }}>{label}</div>
                        <div style={{ fontSize:big?22:13, fontWeight:800,
                          fontFamily:"JetBrains Mono,monospace", color, whiteSpace:"pre-line",
                          lineHeight:1.6 }}>{val}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginBottom:22 }}>
                    <div style={{ display:"flex", justifyContent:"space-between",
                      fontSize:11, color:T.muted, marginBottom:6 }}>
                      <span>vs 8-hour workday</span>
                      <span style={{ fontWeight:700,
                        color:pct>=100?T.green:pct>=50?T.amber:T.muted }}>
                        {pct.toFixed(0)}%
                      </span>
                    </div>
                    <div style={{ height:8, background:T.toggle_bg, borderRadius:4, overflow:"hidden" }}>
                      <div style={{ height:"100%", borderRadius:4, transition:"width 0.6s",
                        width:`${pct}%`,
                        background:`linear-gradient(90deg,${T.cyan},${pct>=100?T.green:pct>=50?T.purple:T.cyan})` }}/>
                    </div>
                  </div>
                  {apps.length > 0 && (
                    <div>
                      <div style={{ fontSize:11, color:T.muted, fontWeight:700,
                        textTransform:"uppercase", letterSpacing:"0.08em",
                        marginBottom:12, display:"flex", alignItems:"center", gap:6 }}>
                        <Monitor size={11}/> App Usage Breakdown
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                        {apps.map((app, i) => (
                          <div key={i} style={{ background:T.toggle_bg, borderRadius:10,
                            padding:"12px 14px" }}>
                            <div style={{ display:"flex", justifyContent:"space-between",
                              alignItems:"center", marginBottom:8 }}>
                              <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                                <span style={{ fontSize:18, lineHeight:1 }}>{getAppIcon(app.app)}</span>
                                <div>
                                  <div style={{ fontSize:13, fontWeight:600, color:T.text }}>{app.app}</div>
                                  <div style={{ fontSize:10, color:T.dim, marginTop:1 }}>
                                    {fmtTime(app.first_used)} – {fmtTime(app.last_used)}
                                  </div>
                                </div>
                              </div>
                              <div style={{ textAlign:"right", flexShrink:0 }}>
                                <div style={{ fontSize:14, fontWeight:800,
                                  fontFamily:"JetBrains Mono,monospace",
                                  color:APP_COLORS[i%APP_COLORS.length] }}>
                                  {fmtSecs(app.seconds)}
                                </div>
                                <div style={{ fontSize:10, color:T.muted, marginTop:1 }}>{app.pct}%</div>
                              </div>
                            </div>
                            <div style={{ height:4, background:T.toggle_bg, borderRadius:2 }}>
                              <div style={{ height:"100%", borderRadius:2,
                                width:`${app.pct}%`, transition:"width 0.5s",
                                background:APP_COLORS[i%APP_COLORS.length], opacity:0.8 }}/>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{ marginTop:14, padding:"10px 14px",
                        background:`${T.cyan}0D`, border:`1px solid ${T.cyan}1A`,
                        borderRadius:10, display:"flex", justifyContent:"space-between",
                        alignItems:"center" }}>
                        <div style={{ fontSize:12, color:T.muted }}>
                          Total · {apps.length} app{apps.length!==1?"s":""} used
                        </div>
                        <div style={{ fontSize:15, fontWeight:800,
                          fontFamily:"JetBrains Mono,monospace", color:T.cyan }}>
                          {fmtSecs(secs)}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// ── Pagination bar ───────────────────────────────────────────────
// Numbered pagination with ellipsis for large page counts (e.g. 600
// employees @ 25/page = 24 pages). Works off whatever list is passed
// in (already filtered/searched) — caller controls the slicing.
function PaginationBar({ page, totalPages, onChange, totalItems, pageSize, T }) {
  if (totalPages <= 1) return null;

  const delta = 2;
  const pages = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || (p >= page - delta && p <= page + delta)) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }

  const start = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const end   = Math.min(page * pageSize, totalItems);

  const btnBase = {
    minWidth:30, height:30, padding:"0 8px", borderRadius:7,
    border:`1px solid ${T.border}`, background:"none", color:T.muted,
    cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:12.5, fontWeight:600,
  };

  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
      padding:"14px 22px", borderTop:`1px solid ${T.border}`, flexWrap:"wrap", gap:10 }}>
      <div style={{ fontSize:12, color:T.muted }}>
        Showing <strong style={{ color:T.text }}>{start}</strong>–<strong style={{ color:T.text }}>{end}</strong> of <strong style={{ color:T.text }}>{totalItems}</strong>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:5 }}>
        <button disabled={page===1} onClick={() => onChange(page - 1)}
          style={{ ...btnBase, opacity: page===1 ? 0.4 : 1, cursor: page===1 ? "not-allowed" : "pointer" }}>
          ‹
        </button>
        {pages.map((p, i) => p === "…" ? (
          <span key={`gap-${i}`} style={{ color:T.dim, fontSize:12, padding:"0 4px" }}>…</span>
        ) : (
          <button key={p} onClick={() => onChange(p)}
            style={{ ...btnBase,
              background: p===page ? T.cyan : "none",
              borderColor: p===page ? T.cyan : T.border,
              color: p===page ? "#fff" : T.muted,
              fontWeight: p===page ? 800 : 600 }}>
            {p}
          </button>
        ))}
        <button disabled={page===totalPages} onClick={() => onChange(page + 1)}
          style={{ ...btnBase, opacity: page===totalPages ? 0.4 : 1, cursor: page===totalPages ? "not-allowed" : "pointer" }}>
          ›
        </button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// DAILY TRACKER
// ═══════════════════════════════════════════════════════════════════
export default function DailyTracker() {
  const { T } = useTheme();

  const STATUSES = [
    { key:"Present",  label:"Present",  color:T.green,    icon:CheckCircle   },
    { key:"Absent",   label:"Absent",   color:T.red,      icon:XCircle       },
    { key:"WFH",      label:"WFH",      color:T.cyan,     icon:Monitor       },
    { key:"Half Day", label:"Half Day", color:T.amber,    icon:AlertCircle   },
    { key:"Late",     label:"Late",     color:T.purple,   icon:Clock         },
    { key:"LOP",      label:"LOP",      color:"#FF8C00",  icon:AlertTriangle },
  ];

  const today = getLocalDate();
  const [selDate,      setSelDate]      = useState(today);
  const [employees,    setEmployees]    = useState([]);
  const [records,      setRecords]      = useState({});
  const [leaveRecords, setLeaveRecords] = useState({});   // employee_id → approved leave
  const [presence,     setPresence]     = useState({});
  const [search,       setSearch]       = useState("");
  const [deptFilter,   setDeptFilter]   = useState("");
  const [departments,  setDepartments]  = useState([]);
  const [saving,       setSaving]       = useState({});
  const [summary,      setSummary]      = useState({});
  const [loading,      setLoading]      = useState(true);
  const [bulkModal,    setBulkModal]    = useState(false);
  const [bulkCheckIn,  setBulkCheckIn]  = useState("09:00");
  const [bulkCheckOut, setBulkCheckOut] = useState("19:00");
  const [bulkPreview,  setBulkPreview]  = useState(null);   // computed on modal open
  const [bulkRunning,  setBulkRunning]  = useState(false);

  // ── Pagination ──────────────────────────────────────────────────
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);

  // ── Fetch attendance + approved leaves ──────────────────────────
  const fetchData = async () => {
    try {
      setLoading(true);
      const [eRes, aRes, dRes, lvRes] = await Promise.allSettled([
        axios.get(`${APIConstant.employees}?status=Active`),
        axios.get(`${APIConstant.attendance}?date=${selDate}`),
        axios.get(APIConstant.departments),
        // Fetch approved leave requests that overlap with selected date
        // Use APIConstant.leaveRequests — same endpoint LeaveRequests.jsx uses.
        // Pass auth header explicitly (DailyTracker has no global ax() helper).
        axios.get(`${APIConstant.leaveRequests}?status=Approved`, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` },
        }).catch(() => ({ data: [] })),
      ]);

      const empList  = eRes.status  === "fulfilled" ? (eRes.value.data  || []) : [];
      const attList  = aRes.status  === "fulfilled" ? (aRes.value.data  || []) : [];
      const deptList = dRes.status  === "fulfilled" ? (dRes.value.data  || []) : [];
      // axios returns { data: [...] } — .catch(() => ({data:[]})) means it always resolves
      const lvList = lvRes.status === "fulfilled" ? (lvRes.value.data || []) : [];
      if (lvRes.status === "rejected") {
        console.warn("[DailyTracker] Leave request fetch failed:", lvRes.reason?.message || lvRes.reason);
      } else {
        console.log(`[DailyTracker] Loaded ${lvList.length} approved leave(s)`);
      }

      setEmployees(empList);
      setDepartments(deptList);

      // Build attendance record map
      const recMap = {};
      for (const r of attList) recMap[r.employee_id] = r;
      setRecords(recMap);

      // Build approved leave map — string comparison, handles both field name variants.
      // LeaveRequests.jsx shows r.start_date || r.from_date (backend may use either).
      // A leave covers selDate if: from_date <= selDate <= to_date
      const lvMap = {};
      for (const lv of lvList) {
        const from = lv.from_date  || lv.start_date || "";
        const to   = lv.to_date    || lv.end_date   || "";
        if (from && to && from <= selDate && selDate <= to) {
          lvMap[lv.employee_id] = lv;
          console.log(`[DailyTracker] Leave match: emp ${lv.employee_id} — ${lv.leave_type||lv.leave_type_name||"leave"} (${from}→${to})`);
        }
      }
      setLeaveRecords(lvMap);

      // ── Auto-fix: correct "Present" records when approved leave exists ─
      // LOP leaves → "LOP" attendance status; normal leaves → "Absent"
      const conflictFixes = [];
      for (const [empId, lv] of Object.entries(lvMap)) {
        const rec = recMap[Number(empId)];
        if (rec && rec.status === "Present") {
          const fixedStatus = lv.is_lop ? "LOP" : "Absent";
          conflictFixes.push(
            axios.put(`${APIConstant.attendance}${rec.id}`, { status: fixedStatus })
              .then(r => { recMap[Number(empId)] = r.data; })
              .catch(err => console.warn(`[DailyTracker] Could not fix emp ${empId}:`, err))
          );
        }
      }
      if (conflictFixes.length > 0) await Promise.all(conflictFixes);

      // Summary counts (attendance + leave)
      const s = {};
      STATUSES.forEach(st => s[st.key] = 0);
      Object.values(recMap).forEach(r => { if (s[r.status] !== undefined) s[r.status]++; });
      setSummary(s);

    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchPresence = async () => {
    if (selDate !== today) return;
    try {
      const token = getAuthToken();
      const res   = await fetch(`${apiBaseUrl}/presence/team`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      if (data.employees) {
        const map = {};
        data.employees.forEach(e => { map[e.employee_id] = e; });
        setPresence(map);
      }
    } catch(e) {}
  };

  useEffect(() => {
    fetchData();
    fetchPresence();
  }, [selDate]);

  // ── Filtered list ────────────────────────────────────────────────
  const filtered = useMemo(() => employees.filter(e => {
    const matchS = !search || `${e.first_name} ${e.last_name} ${e.employee_code}`
      .toLowerCase().includes(search.toLowerCase());
    const matchD = !deptFilter || String(e.department_id) === deptFilter;
    return matchS && matchD;
  }), [employees, search, deptFilter]);

  // Reset to page 1 whenever the search term, department filter, or
  // selected date changes, so the list doesn't land on an out-of-range page.
  useEffect(() => { setPage(1); }, [search, deptFilter, selDate]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  // Clamp page in case filtered shrinks below the current page
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);

  const paginated = useMemo(() => {
    const startIdx = (page - 1) * PAGE_SIZE;
    return filtered.slice(startIdx, startIdx + PAGE_SIZE);
  }, [filtered, page]);

  // ── Smart bulk preview computation ──────────────────────────────
  // Called when the modal opens — categorises every filtered employee.
  const computePreview = useCallback(() => {
    const toFull      = [];   // No record → POST check_in + check_out
    const toCheckout  = [];   // Has check_in, missing check_out → PUT checkout only
    const skipLeave   = [];   // Has approved leave → skip
    const skipDone    = [];   // Already has both check_in + check_out → skip

    for (const emp of filtered) {
      // Rule 1: approved leave → skip entirely
      if (leaveRecords[emp.id]) {
        skipLeave.push(emp);
        continue;
      }
      const rec = records[emp.id];
      if (!rec || (!rec.check_in && !rec.check_out)) {
        // Rule 4: no record (or empty record) → full mark
        toFull.push(emp);
      } else if (rec.check_in && rec.check_out) {
        // Rule 3: both times present → skip
        skipDone.push(emp);
      } else {
        // Rule 2: has check_in but no check_out → add checkout only
        toCheckout.push(emp);
      }
    }
    return { toFull, toCheckout, skipLeave, skipDone };
  }, [filtered, records, leaveRecords]);

  const openBulkModal = () => {
    setBulkPreview(computePreview());
    setBulkModal(true);
  };

  // ── Smart bulk mark ──────────────────────────────────────────────
  const bulkMark = async () => {
    const preview = bulkPreview || computePreview();
    const { toFull, toCheckout } = preview;
    if (toFull.length === 0 && toCheckout.length === 0) {
      setBulkModal(false);
      return;
    }
    setBulkRunning(true);
    try {
      // ── A: Full mark employees (no existing record) ──
      if (toFull.length > 0) {
        await axios.post(`${APIConstant.attendance}bulk-mark`, {
          date:         selDate,
          employee_ids: toFull.map(e => e.id),
          status:       "Present",
          check_in:     bulkCheckIn,
          check_out:    bulkCheckOut,
        });
      }

      // ── B: Checkout-only employees (preserve existing check_in) ──
      // Done sequentially to avoid race conditions on small sets
      for (const emp of toCheckout) {
        const rec = records[emp.id];
        if (!rec) continue;
        await axios.put(`${APIConstant.attendance}${rec.id}`, {
          check_out: bulkCheckOut,
          // preserve existing status; default to Present if somehow blank
          status: rec.status || "Present",
        });
      }

      fetchData();
    } catch(e) { console.error("Bulk mark error:", e); }
    finally {
      setBulkRunning(false);
      setBulkModal(false);
      setBulkPreview(null);
    }
  };

  // ── Individual attendance mark ───────────────────────────────────
  const markAttendance = async (emp, status) => {
    setSaving(p => ({ ...p, [emp.id]: true }));
    try {
      const existing = records[emp.id];
      if (existing) {
        // Update status; only set check_out if missing; never overwrite check_in
        const payload = { status };
        if (!existing.check_out) payload.check_out = "17:00";
        const r = await axios.put(`${APIConstant.attendance}${existing.id}`, payload);
        setRecords(p => ({ ...p, [emp.id]: r.data }));
        fetchData();
      } else {
        const r = await axios.post(APIConstant.attendance, {
          employee_id: emp.id,
          date:        selDate,
          status,
          check_in:   "09:00",
          check_out:  "17:00",
        });
        setRecords(p => ({ ...p, [emp.id]: r.data }));
        fetchData();
      }
    } catch(e) { console.error(e); }
    finally { setSaving(p => ({ ...p, [emp.id]: false })); }
  };

  const updateTime = async (emp, field, val) => {
    const existing = records[emp.id];
    if (!existing) return;
    try {
      const r = await axios.put(`${APIConstant.attendance}${existing.id}`, { [field]: val });
      setRecords(p => ({ ...p, [emp.id]: r.data }));
      fetchData();
    } catch(e) { console.error(e); }
  };

  const isToday = selDate === today;

  // ── Excel download — Daily Tracker ────────────────────────────────
  // Exports the currently filtered/searched employee list for selDate,
  // including status, leave info, check-in/out and work hours.
  const downloadExcel = () => {
    if (filtered.length === 0) { alert("No employees to export."); return; }

    const rows = filtered.map(emp => {
      const rec = records[emp.id];
      const lv  = leaveRecords[emp.id];
      const statusVal = rec?.status || (lv ? (lv.is_lop ? "LOP" : "On Leave") : "Not Marked");
      return {
        "Employee Code": emp.employee_code || "",
        "First Name":    emp.first_name || "",
        "Last Name":     emp.last_name || "",
        "Department":    emp.department_name || "",
        "Date":          selDate,
        "Status":        statusVal,
        "Leave Type":    lv ? (lv.leave_type || lv.leave_type_name || "") : "",
        "LOP Days":      lv?.lop_days ?? "",
        "Check In":      rec?.check_in || "",
        "Check Out":     rec?.check_out || "",
        "Work Hours":    rec?.work_hours ?? "",
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0]).map(() => ({ wch: 18 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance");
    XLSX.writeFile(wb, `Daily_Attendance_${selDate}.xlsx`);
  };

  const PresenceDot = ({ employeeId }) => {
    const p = presence[employeeId];
    if (!p || !isToday) return null;
    const color = p.status === "active" ? T.green : p.status === "idle" ? T.amber : null;
    if (!color) return null;
    return (
      <div title={`${p.status} — ${p.active_app || ""}`}
        style={{ width:7, height:7, borderRadius:"50%", background:color,
          flexShrink:0, boxShadow:`0 0 4px ${color}`,
          animation:p.status==="active"?"pulse 1.5s infinite":"none" }}/>
    );
  };

  // ── Inline select style ──────────────────────────────────────────
  const selStyle = {
    background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:8,
    color:T.text, fontSize:13, padding:"7px 12px", outline:"none",
    fontFamily:"Outfit,sans-serif",
  };

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>TIME & ATTENDANCE</div>
          <h2>Daily Attendance Tracker</h2>
          <p>Mark and manage employee attendance day-wise</p>
        </div>
        <div className={styles.headerRight}>
          <input type="date" value={selDate} onChange={e => setSelDate(e.target.value)}
            style={{ background:T.inp_bg, border:`1px solid ${T.cyan}26`,
              borderRadius:8, color:T.text, fontSize:13, padding:"8px 12px",
              outline:"none", fontFamily:"Outfit,sans-serif" }}/>
        </div>
      </div>

      {/* Summary strip */}
      <div style={{ display:"flex", gap:12, marginBottom:20, flexWrap:"wrap" }}>
        {STATUSES.map(({ key, label, color, icon:Icon }) => (
          <div key={key} style={{ display:"flex", alignItems:"center", gap:8,
            background:T.toggle_bg, border:`1px solid ${T.border}`,
            borderRadius:8, padding:"10px 16px", flex:1, minWidth:120 }}>
            <Icon size={15} style={{ color }}/>
            <div>
              <div style={{ fontSize:18, fontWeight:800,
                fontFamily:"JetBrains Mono,monospace", color }}>{summary[key]||0}</div>
              <div style={{ fontSize:11, color:T.muted, textTransform:"uppercase",
                letterSpacing:"0.07em" }}>{label}</div>
            </div>
          </div>
        ))}
        <div style={{ display:"flex", alignItems:"center", gap:8,
          background:T.toggle_bg, border:`1px solid ${T.border}`,
          borderRadius:8, padding:"10px 16px", flex:1, minWidth:120 }}>
          <AlertCircle size={15} style={{ color:T.muted }}/>
          <div>
            <div style={{ fontSize:18, fontWeight:800,
              fontFamily:"JetBrains Mono,monospace", color:T.muted }}>
              {employees.length - Object.keys(records).length}
            </div>
            <div style={{ fontSize:11, color:T.muted, textTransform:"uppercase",
              letterSpacing:"0.07em" }}>Not Marked</div>
          </div>
        </div>
        {/* On Leave count (from leave records) */}
        {Object.keys(leaveRecords).length > 0 && (
          <div style={{ display:"flex", alignItems:"center", gap:8,
            background:`${T.amber}0F`, border:`1px solid ${T.amber}26`,
            borderRadius:8, padding:"10px 16px", flex:1, minWidth:120 }}>
            <AlertCircle size={15} style={{ color:T.amber }}/>
            <div>
              <div style={{ fontSize:18, fontWeight:800,
                fontFamily:"JetBrains Mono,monospace", color:T.amber }}>
                {Object.keys(leaveRecords).length}
              </div>
              <div style={{ fontSize:11, color:T.muted, textTransform:"uppercase",
                letterSpacing:"0.07em" }}>On Leave</div>
            </div>
          </div>
        )}
        {isToday && Object.values(presence).filter(p => p.status === "active").length > 0 && (
          <div style={{ display:"flex", alignItems:"center", gap:8,
            background:`${T.green}0F`, border:`1px solid ${T.green}26`,
            borderRadius:8, padding:"10px 16px", flex:1, minWidth:120 }}>
            <Wifi size={15} style={{ color:T.green }}/>
            <div>
              <div style={{ fontSize:18, fontWeight:800,
                fontFamily:"JetBrains Mono,monospace", color:T.green }}>
                {Object.values(presence).filter(p => p.status === "active").length}
              </div>
              <div style={{ fontSize:11, color:T.muted, textTransform:"uppercase",
                letterSpacing:"0.07em" }}>Online Now</div>
            </div>
          </div>
        )}
      </div>

      {/* Main card */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h3>
            Employee Attendance — {
              parseLocalDate(selDate).toLocaleDateString(undefined, {
                weekday:"long", day:"2-digit", month:"long", year:"numeric" })
            }
          </h3>
          <div style={{ display:"flex", gap:8 }}>
            <button className={styles.btnSm} onClick={downloadExcel}
              style={{ color:T.cyan, borderColor:`${T.cyan}40`, display:"flex", alignItems:"center", gap:5 }}>
              <Download size={12}/> Download Excel
            </button>
            <button className={styles.btnSm}
              style={{ color:T.green, borderColor:`${T.green}40` }}
              onClick={openBulkModal}>
              ✓ Mark All Present
            </button>
            <button className={styles.btnSm} onClick={() => { fetchData(); fetchPresence(); }}>
              <RotateCcw size={12}/> Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display:"flex", gap:10, padding:"14px 22px",
          borderBottom:`1px solid ${T.cyan}0F` }}>
          <div style={{ position:"relative", flex:1 }}>
            <Search size={13} style={{ position:"absolute", left:10, top:"50%",
              transform:"translateY(-50%)", color:T.dim }}/>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search employee…"
              style={{ width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`,
                borderRadius:8, color:T.text, fontSize:13,
                padding:"7px 12px 7px 30px", outline:"none",
                fontFamily:"Outfit,sans-serif", boxSizing:"border-box" }}/>
          </div>
          <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} style={selStyle}>
            <option value="" style={{background:T.opt_bg}}>All Departments</option>
            {departments.map(d => (
              <option key={d.id} value={d.id} style={{background:T.opt_bg}}>{d.name}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className={styles.loader}><div className={styles.spinner}/>Loading…</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.darkTable}>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Status</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Hours</th>
                  <th>
                    <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                      <Monitor size={11} style={{ color:T.muted }}/> Actual Hours
                    </div>
                    <div style={{ fontSize:9, color:T.dim, fontWeight:400,
                      textTransform:"none", letterSpacing:0 }}>via desktop agent</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((emp, i) => {
                  const rec      = records[emp.id];
                  const lv       = leaveRecords[emp.id];   // approved leave (if any)
                  const isSaving = saving[emp.id];
                  const pres     = presence[emp.id];

                  return (
                    <tr key={emp.id}
                      style={{ background: lv ? `${T.amber}05` : "transparent" }}>
                      <td>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <div style={{ width:34, height:34, borderRadius:9,
                            background:emp.avatar_color||COLORS[((page-1)*PAGE_SIZE+i)%COLORS.length],
                            display:"flex", alignItems:"center", justifyContent:"center",
                            fontSize:11, fontWeight:700, color:"#fff", flexShrink:0,
                            position:"relative" }}>
                            {initials(emp.first_name, emp.last_name)}
                            {isToday && pres?.status === "active" && (
                              <div style={{ position:"absolute", bottom:-1, right:-1,
                                width:9, height:9, borderRadius:"50%",
                                background:T.green, border:`2px solid ${T.card}`,
                                boxShadow:`0 0 4px ${T.green}` }}/>
                            )}
                          </div>
                          <div>
                            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                              <span style={{ fontSize:13, fontWeight:500 }}>
                                {emp.first_name} {emp.last_name}
                              </span>
                              <PresenceDot employeeId={emp.id}/>
                            </div>
                            <div style={{ fontSize:11, color:T.muted }}>{emp.employee_code}</div>
                          </div>
                        </div>
                      </td>
                      <td className={styles.dim}>{emp.department_name||"—"}</td>

                      {/* Status column — shows leave badge OR status buttons */}
                      <td>
                        {lv ? (
                          // ── Approved leave badge ──────────────────────────
                          <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                            <span style={{ fontSize:11, fontWeight:700,
                              padding:"3px 10px", borderRadius:20,
                              background:`${T.amber}20`, color:T.amber,
                              border:`1px solid ${T.amber}40`,
                              whiteSpace:"nowrap" }}>
                              {lv.is_lop ? "⚠️" : "🏖"} {
                              lv.is_lop && (lv.lop_days > 0)
                                ? `${lv.leave_type || "Leave"} (${lv.lop_days}d LOP)`
                                : lv.is_lop
                                  ? `${lv.leave_type || "Leave"} (LOP)`
                                  : (lv.leave_type || lv.leave_type_name || "Approved Leave")
                            }
                            </span>
                            {/* Still allow HR to override if needed */}
                            <div style={{ display:"flex", gap:3, flexWrap:"wrap" }}>
                              {STATUSES.map(({ key, label, color }) => (
                                <button key={key}
                                  onClick={() => markAttendance(emp, key)}
                                  disabled={isSaving}
                                  title={`Override to ${label}`}
                                  style={{ fontSize:10, fontWeight:600,
                                    padding:"2px 7px", borderRadius:5,
                                    border:`1px solid ${rec?.status===key?color:T.toggle_bg}`,
                                    background:rec?.status===key?`${color}20`:"transparent",
                                    color:rec?.status===key?color:T.dim,
                                    cursor:"pointer", fontFamily:"Outfit,sans-serif",
                                    opacity:0.7 }}>
                                  {label}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : (
                          // ── Normal status buttons ─────────────────────────
                          <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                            {STATUSES.map(({ key, label, color }) => (
                              <button key={key}
                                onClick={() => markAttendance(emp, key)}
                                disabled={isSaving}
                                style={{ fontSize:10.5, fontWeight:600,
                                  padding:"3px 8px", borderRadius:6,
                                  border:`1px solid ${rec?.status===key?color:T.toggle_bg}`,
                                  background:rec?.status===key?`${color}20`:"transparent",
                                  color:rec?.status===key?color:T.muted,
                                  cursor:"pointer", fontFamily:"Outfit,sans-serif",
                                  transition:"all 0.15s" }}>
                                {label}
                              </button>
                            ))}
                          </div>
                        )}
                      </td>

                      {/* Check In */}
                      <td>
                        {rec ? (
                          <input type="time" value={rec.check_in||""}
                            onChange={e => updateTime(emp, "check_in", e.target.value)}
                            style={{ background:T.inp_bg, border:`1px solid ${T.border}`,
                              borderRadius:7, color:T.text, fontSize:12,
                              padding:"5px 8px", outline:"none", width:90,
                              fontFamily:"Outfit,sans-serif" }}/>
                        ) : <span style={{ color:T.dim, fontSize:12 }}>—</span>}
                      </td>

                      {/* Check Out */}
                      <td>
                        {rec ? (
                          <input type="time" value={rec.check_out||""}
                            onChange={e => updateTime(emp, "check_out", e.target.value)}
                            style={{ background:T.inp_bg, border:`1px solid ${T.border}`,
                              borderRadius:7, color:T.text, fontSize:12,
                              padding:"5px 8px", outline:"none", width:90,
                              fontFamily:"Outfit,sans-serif" }}/>
                        ) : <span style={{ color:T.dim, fontSize:12 }}>—</span>}
                      </td>

                      {/* Hours */}
                      <td>
                        {rec?.work_hours > 0 ? (
                          <span style={{ fontFamily:"JetBrains Mono,monospace", fontSize:12,
                            color:rec.work_hours>=8?T.green:rec.work_hours>=4?T.amber:T.red }}>
                            {rec.work_hours}h
                          </span>
                        ) : <span style={{ color:T.dim, fontSize:12 }}>—</span>}
                      </td>

                      {/* Actual hours (desktop agent) */}
                      <td>
                        <ActualHoursCell
                          employeeId={emp.id}
                          empName={`${emp.first_name} ${emp.last_name}`}
                          date={selDate}
                        />
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr className={styles.emptyRow}>
                    <td colSpan={7}>No employees found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {!loading && (
          <PaginationBar
            page={page}
            totalPages={totalPages}
            onChange={setPage}
            totalItems={filtered.length}
            pageSize={PAGE_SIZE}
            T={T}
          />
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════
          SMART BULK MARK MODAL
          Shows a breakdown of what will actually happen before confirm.
      ══════════════════════════════════════════════════════════════ */}
      {bulkModal && bulkPreview && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)",
          zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center",
          fontFamily:"Outfit,sans-serif" }}
          onClick={e => { if(e.target===e.currentTarget && !bulkRunning) setBulkModal(false); }}>
          <div style={{ background:T.card, border:`1px solid ${T.green}40`,
            borderRadius:14, width:440, padding:28, boxShadow:T.shadow }}>

            {/* Header */}
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
              <CheckCircle size={20} style={{ color:T.green, flexShrink:0 }}/>
              <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:T.text }}>
                Smart Bulk Mark — Present
              </h3>
            </div>
            <p style={{ fontSize:13, color:T.muted, margin:"0 0 20px", lineHeight:1.6 }}>
              Here's exactly what will happen to your filtered employees:
            </p>

            {/* Breakdown rows */}
            <div style={{ display:"flex", flexDirection:"column", gap:9, marginBottom:22 }}>

              {/* Full mark */}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                padding:"10px 14px", borderRadius:10,
                background: bulkPreview.toFull.length > 0 ? `${T.green}0F` : T.toggle_bg,
                border:`1px solid ${bulkPreview.toFull.length > 0 ? T.green+"26" : T.toggle_bg}` }}>
                <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                  <span style={{ fontSize:15 }}>✅</span>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600,
                      color:bulkPreview.toFull.length > 0 ? T.green : T.dim }}>
                      Full check-in + check-out
                    </div>
                    <div style={{ fontSize:11, color:T.dim, marginTop:1 }}>
                      No attendance record yet
                    </div>
                  </div>
                </div>
                <span style={{ fontSize:18, fontWeight:800,
                  fontFamily:"JetBrains Mono,monospace",
                  color:bulkPreview.toFull.length > 0 ? T.green : T.dim }}>
                  {bulkPreview.toFull.length}
                </span>
              </div>

              {/* Checkout only */}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                padding:"10px 14px", borderRadius:10,
                background:bulkPreview.toCheckout.length > 0 ? `${T.cyan}0F` : T.toggle_bg,
                border:`1px solid ${bulkPreview.toCheckout.length > 0 ? T.cyan+"26" : T.toggle_bg}` }}>
                <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                  <span style={{ fontSize:15 }}>⏱</span>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600,
                      color:bulkPreview.toCheckout.length > 0 ? T.cyan : T.dim }}>
                      Checkout time only added
                    </div>
                    <div style={{ fontSize:11, color:T.dim, marginTop:1 }}>
                      Already have check-in — will not be changed
                    </div>
                  </div>
                </div>
                <span style={{ fontSize:18, fontWeight:800,
                  fontFamily:"JetBrains Mono,monospace",
                  color:bulkPreview.toCheckout.length > 0 ? T.cyan : T.dim }}>
                  {bulkPreview.toCheckout.length}
                </span>
              </div>

              {/* Skip — approved leave */}
              {bulkPreview.skipLeave.length > 0 && (
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                  padding:"10px 14px", borderRadius:10,
                  background:`${T.amber}0F`,
                  border:`1px solid ${T.amber}26` }}>
                  <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                    <span style={{ fontSize:15 }}>🏖</span>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:T.amber }}>
                        Skipped — Approved Leave
                      </div>
                      <div style={{ fontSize:11, color:T.dim, marginTop:1 }}>
                        {bulkPreview.skipLeave.map(e => `${e.first_name} ${e.last_name}`).join(", ")}
                      </div>
                    </div>
                  </div>
                  <span style={{ fontSize:18, fontWeight:800,
                    fontFamily:"JetBrains Mono,monospace", color:T.amber }}>
                    {bulkPreview.skipLeave.length}
                  </span>
                </div>
              )}

              {/* Skip — already complete */}
              {bulkPreview.skipDone.length > 0 && (
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                  padding:"10px 14px", borderRadius:10,
                  background:T.toggle_bg, border:`1px solid ${T.border}` }}>
                  <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                    <span style={{ fontSize:15 }}>✓</span>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:T.dim }}>
                        Skipped — Already complete
                      </div>
                      <div style={{ fontSize:11, color:T.dim, marginTop:1 }}>
                        Both check-in and check-out already set
                      </div>
                    </div>
                  </div>
                  <span style={{ fontSize:18, fontWeight:800,
                    fontFamily:"JetBrains Mono,monospace", color:T.dim }}>
                    {bulkPreview.skipDone.length}
                  </span>
                </div>
              )}
            </div>

            {/* Time inputs — only shown if there are employees to actually mark */}
            {(bulkPreview.toFull.length > 0 || bulkPreview.toCheckout.length > 0) && (
              <div style={{ display:"grid",
                gridTemplateColumns: bulkPreview.toFull.length > 0 ? "1fr 1fr" : "1fr",
                gap:14, marginBottom:22 }}>
                {bulkPreview.toFull.length > 0 && (
                  <div>
                    <label style={{ fontSize:11, color:T.muted, display:"block",
                      marginBottom:6, textTransform:"uppercase", letterSpacing:"0.07em" }}>
                      Check-In Time
                    </label>
                    <input type="time" value={bulkCheckIn}
                      onChange={e => setBulkCheckIn(e.target.value)}
                      style={{ width:"100%", background:T.inp_bg,
                        border:`1px solid ${T.border}`, borderRadius:8,
                        color:T.text, fontSize:14, padding:"9px 10px",
                        outline:"none", fontFamily:"Outfit,sans-serif",
                        boxSizing:"border-box" }}/>
                    <div style={{ fontSize:10, color:T.dim, marginTop:4 }}>
                      Applied to {bulkPreview.toFull.length} employee{bulkPreview.toFull.length!==1?"s":""}
                    </div>
                  </div>
                )}
                <div>
                  <label style={{ fontSize:11, color:T.muted, display:"block",
                    marginBottom:6, textTransform:"uppercase", letterSpacing:"0.07em" }}>
                    Check-Out Time
                  </label>
                  <input type="time" value={bulkCheckOut}
                    onChange={e => setBulkCheckOut(e.target.value)}
                    style={{ width:"100%", background:T.inp_bg,
                      border:`1px solid ${T.border}`, borderRadius:8,
                      color:T.text, fontSize:14, padding:"9px 10px",
                      outline:"none", fontFamily:"Outfit,sans-serif",
                      boxSizing:"border-box" }}/>
                  <div style={{ fontSize:10, color:T.dim, marginTop:4 }}>
                    Applied to {bulkPreview.toFull.length + bulkPreview.toCheckout.length} employee
                    {(bulkPreview.toFull.length + bulkPreview.toCheckout.length)!==1?"s":""}
                  </div>
                </div>
              </div>
            )}

            {/* Nothing to do message */}
            {bulkPreview.toFull.length === 0 && bulkPreview.toCheckout.length === 0 && (
              <div style={{ textAlign:"center", padding:"14px 0 22px",
                fontSize:13, color:T.muted }}>
                Nothing to update — all employees are either already marked or on approved leave.
              </div>
            )}

            {/* Buttons */}
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={() => setBulkModal(false)} disabled={bulkRunning}
                style={{ padding:"9px 18px", borderRadius:8,
                  border:`1px solid ${T.border}`, background:"none",
                  color:T.muted, cursor:"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:600,
                  opacity:bulkRunning?0.5:1 }}>
                Cancel
              </button>
              <button onClick={bulkMark} disabled={bulkRunning ||
                  (bulkPreview.toFull.length === 0 && bulkPreview.toCheckout.length === 0)}
                style={{ padding:"9px 20px", borderRadius:8, border:"none",
                  background: (bulkPreview.toFull.length === 0 && bulkPreview.toCheckout.length === 0)
                    ? T.toggle_bg : T.green,
                  color: (bulkPreview.toFull.length === 0 && bulkPreview.toCheckout.length === 0)
                    ? T.dim : "#fff",
                  cursor:(bulkPreview.toFull.length === 0 && bulkPreview.toCheckout.length === 0)
                    ? "not-allowed" : "pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700,
                  opacity:bulkRunning?0.7:1 }}>
                {bulkRunning ? "Processing…" :
                  (bulkPreview.toFull.length === 0 && bulkPreview.toCheckout.length === 0)
                    ? "Nothing to do"
                    : `✓ Confirm (${bulkPreview.toFull.length + bulkPreview.toCheckout.length} employees)`}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
    </div>
  );
}